/**
 * La consulta rápida: lo que pasa al leer un código en una pantalla que no
 * los usa (2026-10-03).
 *
 * El dueño pidió que el lector funcionara «en todos lados». En Vender, el
 * código entra a la venta; en Catálogo e Inventario, se busca. En cualquier
 * otra pantalla —el Inicio, los Reportes, la Caja— sale esto: el producto con
 * su precio, y lo que se suele querer hacer con él (venderlo o verlo en el
 * catálogo). Si se lee otro con esta ventana abierta, se consulta ese.
 *
 * El precio sale de la misma consulta que el Consultor de precios: lo que
 * cobra la caja, con impuesto, y su equivalente en las otras monedas.
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PackageSearch, ShoppingCart } from "lucide-react";
import { Boton, Cargando, Nota } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { mensajeDeError } from "@/lib/api";
import { consultor, monedas } from "@/lib/endpoints";
import { useAlLeerCodigo } from "@/lib/lectorGlobal";
import { comoFormato, formatMoney, monedaPrincipal, redondearMoneda, toNumber } from "@/lib/money";
import { useAuth } from "@/stores/auth";

export function ConsultaRapida({
  codigo,
  onOtro,
  onCerrar,
}: {
  codigo: string;
  /** Se leyó otro código con la ventana abierta. */
  onOtro: (codigo: string) => void;
  onCerrar: () => void;
}) {
  const navegar = useNavigate();
  const { puede } = useAuth();
  useAlLeerCodigo(onOtro, { dentroDeVentana: true });

  const { data, isLoading, error } = useQuery({
    queryKey: ["consulta-rapida", codigo],
    queryFn: () => consultor.buscar(codigo),
    retry: false,
  });
  const { data: misMonedas } = useQuery({ queryKey: ["monedas-negocio"], queryFn: monedas.listar });
  const principal = misMonedas ? monedaPrincipal(misMonedas) : undefined;
  const otras = (misMonedas ?? []).filter((m) => m.is_active && !m.is_primary && m.current_rate);

  const producto = data?.encontrado ?? (data?.parecidos.length === 1 ? data.parecidos[0]! : null);
  const precio = producto ? toNumber(producto.total ?? producto.precio) : 0;

  const ir = (a: string, estado: Record<string, string>) => {
    onCerrar();
    navegar(a, { state: estado });
  };

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Consulta rápida"
      descripcion={`Código leído: ${codigo}`}
      ancho="sm"
      pie={
        <div className="flex flex-wrap justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cerrar
          </Boton>
          <Boton
            variante="secundario"
            icono={<PackageSearch size={16} />}
            onClick={() => ir("/catalogo", { busqueda: codigo })}
          >
            Ver en el catálogo
          </Boton>
          {producto && puede("pos.sell") && (
            <Boton icono={<ShoppingCart size={16} />} onClick={() => ir("/vender", { codigo })}>
              Vender
            </Boton>
          )}
        </div>
      }
    >
      {isLoading ? (
        <Cargando texto="Buscando el producto…" />
      ) : error ? (
        <Nota tono="peligro">{mensajeDeError(error)}</Nota>
      ) : !producto ? (
        <Nota tono="aviso">
          {data && data.parecidos.length > 1
            ? `El código coincide con ${data.parecidos.length} productos. Consúltelos en el catálogo.`
            : "No hay ningún producto registrado con este código."}
        </Nota>
      ) : (
        <div className="text-center">
          {producto.categoria && (
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">{producto.categoria}</p>
          )}
          <p className="mt-1 text-lg font-semibold text-ink text-balance">{producto.nombre}</p>
          {producto.presentacion && <p className="text-sm text-muted">{producto.presentacion}</p>}
          {principal && (
            <p className="tabular mt-3 text-4xl font-black tracking-tight text-ink">
              {formatMoney(precio, comoFormato(principal))}
            </p>
          )}
          {otras.length > 0 && (
            <p className="tabular mt-1 text-lg font-semibold text-ink-soft">
              {otras
                .map((m) => formatMoney(redondearMoneda(precio * toNumber(m.current_rate), m), comoFormato(m)))
                .join(" · ")}
            </p>
          )}
          <p className="mt-3 text-xs text-muted">Puede leer otro producto sin cerrar esta ventana.</p>
        </div>
      )}
    </Modal>
  );
}
